import { useState, useEffect } from 'react';
import type { Message, LocationData } from '../types';

function formatTime(unix: number): string {
  return new Date(unix * 1000).toLocaleTimeString('id', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(unix: number): string {
  const d = new Date(unix * 1000);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Hari ini';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Kemarin';
  return d.toLocaleDateString('id', { weekday: 'long', day: 'numeric', month: 'long' });
}

// Lightweight map preview (Zero WebGL dependency to avoid context loss errors)
function LocationMessage({ content }: { content: string }) {
  const [addr, setAddr] = useState<string | null>(null);
  let loc: LocationData | null = null;
  try { loc = JSON.parse(content) as LocationData; } catch {}

  useEffect(() => {
    if (!loc) return;
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${loc.lat}&lon=${loc.lng}&format=json`)
      .then(r => r.json())
      .then((d: { display_name?: string }) => setAddr(d.display_name || null))
      .catch(() => {});
  }, [content]);

  if (!loc) return <span className="text-sm opacity-70">Lokasi tidak valid</span>;

  // Compute OpenStreetMap raster tile coordinates (zoom level 15)
  const zoom = 15;
  const n = Math.pow(2, zoom);
  const xtile = Math.floor(((loc.lng + 180) / 360) * n);
  const latRad = (loc.lat * Math.PI) / 180;
  const ytile = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
  const tileUrl = `https://tile.openstreetmap.org/${zoom}/${xtile}/${ytile}.png`;

  const openUrl = `https://www.openstreetmap.org/?mlat=${loc.lat}&mlon=${loc.lng}#map=15/${loc.lat}/${loc.lng}`;

  return (
    <a
      href={openUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-xl overflow-hidden hover:opacity-95 transition-all group border border-white/10"
      style={{ width: 240 }}
    >
      <div className="relative h-32 bg-dark-800 overflow-hidden flex items-center justify-center">
        {/* Raster Tile Map Image */}
        <img
          src={tileUrl}
          alt="Peta Lokasi"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          onError={(e) => {
            (e.currentTarget as HTMLElement).style.display = 'none';
          }}
        />
        {/* Map Marker Pin */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative -mt-3">
            <svg className="w-8 h-8 text-red-500 drop-shadow-md" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
          </div>
        </div>
      </div>

      <div className="px-3 py-2 bg-dark-800/95 flex flex-col gap-0.5 border-t border-white/5">
        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-brand-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <span className="text-xs font-semibold text-white truncate">Lokasi Dibagikan</span>
        </div>
        <span className="text-[11px] text-dark-300 truncate pl-5">
          {addr || `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}`}
        </span>
      </div>
    </a>
  );
}

interface MessageBubbleProps {
  message: Message;
  isMine: boolean;
  showAvatar: boolean;
  onImageClick: (url: string) => void;
}

// We use a Map keyed by roomId so date separators don't leak between rooms.
// If MessageBubble ever gets roomId prop, use it directly instead.
// For now, reset is managed by the parent re-mounting on room switch.
const dateCache = new Map<string, string>();

export function MessageBubble({ message, isMine, showAvatar, onImageClick }: MessageBubbleProps) {
  const [msgDate] = useState(formatDate(message.created_at));
  const [showDate, setShowDate] = useState(true);

  // Track date separators per-room to avoid leakage between rooms
  useEffect(() => {
    const last = dateCache.get(message.room_id);
    const shouldShow = last !== msgDate;
    if (shouldShow) {
      dateCache.set(message.room_id, msgDate);
    }
    setShowDate(shouldShow);
  }, [message.room_id, message.created_at, msgDate]);

  return (
    <>
      {showDate && (
        <div className="flex items-center justify-center my-4">
          <div className="px-3 py-1 bg-dark-700 rounded-full text-xs text-dark-400">
            {msgDate}
          </div>
        </div>
      )}

      <div className={`flex items-end gap-2 message-animate ${isMine ? 'flex-row-reverse' : 'flex-row'} mb-1`}>
        {/* Avatar */}
        {!isMine && (
          <div className="w-7 flex-shrink-0">
            {showAvatar ? (
              message.user?.avatar_url ? (
                <img src={message.user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                  {(message.user?.name || '?')[0]}
                </div>
              )
            ) : null}
          </div>
        )}

        <div className={`flex flex-col gap-1 max-w-[75%] ${isMine ? 'items-end' : 'items-start'}`}>
          {/* Sender name (group chats) */}
          {!isMine && showAvatar && message.user && (
            <span className="text-xs text-dark-400 px-1">{message.user.name}</span>
          )}

          {/* Bubble */}
          <div className={isMine ? 'message-bubble-sent' : 'message-bubble-recv'}>
            {message.type === 'text' && (
              <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
            )}

            {message.type === 'image' && (
              <div className="-m-2.5 -mt-2.5 overflow-hidden rounded-2xl" style={{ maxWidth: 240 }}>
                <img
                  src={message.file_url || message.content}
                  alt={message.file_name || 'Foto'}
                  className="w-full cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ maxHeight: 300, objectFit: 'cover' }}
                  onClick={() => onImageClick(message.file_url || message.content)}
                  loading="lazy"
                />
                {message.file_name && (
                  <div className="px-3 py-1.5 text-xs opacity-70 truncate">{message.file_name}</div>
                )}
              </div>
            )}

            {message.type === 'location' && (
              <div className="-m-2.5 overflow-hidden rounded-2xl">
                <LocationMessage content={message.content} />
              </div>
            )}

            {message.type === 'system' && (
              <p className="text-xs italic opacity-60">{message.content}</p>
            )}
          </div>

          {/* Timestamp */}
          <span className="text-[10px] text-dark-500 px-1">{formatTime(message.created_at)}</span>
        </div>
      </div>
    </>
  );
}
